import type { ParamSchema } from '@edge/charts-indicators'

/**
 * How indicator params lay out in the settings form.
 *
 * The catalog's `params` array index IS the positional calcParams slot, so the
 * array can never be reordered without invalidating every persisted layout.
 * These helpers let the FORM diverge from that array: `order` moves a control
 * without moving its slot, and `showWhen` hides a control that is meaningless
 * given another param's current value (an MA period when the source is not a
 * moving average) while its slot stays intact.
 */

export interface ParamSlot {
  schema: ParamSchema
  /** Original array position: the calcParams index to read and write. */
  index: number
}

/** Params in display order, each carrying the slot it still owns. */
export function orderedParamSlots(params: readonly ParamSchema[]): ParamSlot[] {
  return params
    .map((schema, index) => ({ schema, index }))
    .sort((a, b) => {
      // Undeclared params keep their array position, so a param with an
      // explicit order competes against the position of its neighbours.
      const ao = a.schema.order ?? a.index
      const bo = b.schema.order ?? b.index
      if (ao !== bo) return ao - bo
      return a.index - b.index // stable for ties
    })
}

/** Whether a control should render at all, given the current calcParams. */
export function isParamVisible(schema: ParamSchema, calcParams: readonly unknown[]): boolean {
  if (schema.hidden) return false
  const cond = schema.showWhen
  if (!cond) return true
  return cond.in.includes(calcParams[cond.index])
}

/** A titled run of controls (a script's Pine `group`) laid out in rows. */
export interface ParamSection {
  group?: string
  /** Each row's controls; more than one when they share an `inline` id. */
  rows: ParamSlot[][]
}

/**
 * The form's sections, in display order: params sharing a `group` gather under one
 * header placed where the first of them is (ungrouped params between groups form their
 * own untitled sections), and within a section params sharing an `inline` id share the
 * row of the first one. Every slot keeps its calcParams index.
 */
export function paramSections(slots: readonly ParamSlot[]): ParamSection[] {
  const sections: ParamSection[] = []
  const byGroup = new Map<string, ParamSection>()
  let loose: ParamSection | undefined
  for (const slot of slots) {
    const group = slot.schema.group
    let section: ParamSection
    if (group !== undefined) {
      section = byGroup.get(group) ?? { group, rows: [] }
      if (!byGroup.has(group)) {
        byGroup.set(group, section)
        sections.push(section)
      }
      loose = undefined
    } else {
      if (!loose) sections.push((loose = { rows: [] }))
      section = loose
    }
    const inline = slot.schema.inline
    const row =
      inline !== undefined ? section.rows.find((r) => r[0]!.schema.inline === inline) : undefined
    if (row) row.push(slot)
    else section.rows.push([slot])
  }
  return sections
}
